'use client';
import { useRef } from 'react';
import { Text } from '../../Text';
import { Anchor } from '../../Anchor';
import { IconResolver } from '../../IconResolver';
import { Media } from '../../Media';
import { Card } from '../../Card';
import type { NavDropdownProps } from './NavDropdownMobile';

type TabDataProps = NavDropdownProps & {
	featuredText?: string;
	setActiveTab(value: number | undefined): void;
};

export function MobileTabData(props: TabDataProps) {
	const dataRef = useRef<HTMLDivElement>(null);
	const theme = 'light';

	const featuredText = props.featuredText ?? 'Featured';

	return (
		<div ref={dataRef} data-theme={theme} className='p-spacing-2xs flex flex-col gap-spacing-lg w-fit min-w-full'>
			<Text
				as='button'
				className='flex items-center gap-2 w-fit py-1 -mb-responsive-xs text-text-secondary'
				styleType='label-md'
				onClick={() => props.setActiveTab(undefined)}
			>
				<IconResolver className='w-3 h-3' icon='caret-left' />
				<span>Back</span>
			</Text>
			{props.groups &&
				props.groups.length > 0 &&
				props.groups.map((group, i) => {
					return (
						<div key={`${group.label}-${i}-mobile-tab-data`} data-brand={group.brand} data-theme={theme}>
							<Text as='h3' className='text-text-secondary pb-4 shrink-0 grow-0' styleType='label-md'>
								{group.label}
							</Text>

							<div className='grow-1 flex flex-col gap-spacing-sm '>
								{group.cards && group.cards.length > 0 && (
									<div className='[flex-basis:0] grow-1'>
										{group.cards.map((card, j) => {
											const isExternal: boolean = !(card.href?.startsWith('/') || card.href?.startsWith(process.env.NEXT_PUBLIC_SITE_URL));
											return (
												<Anchor
													className='flex'
													key={`${card.title}-${j}-mobile-tab-data`}
													href={card.href}
													target={isExternal ? '_blank' : undefined}
												>
													<div
														className='rounded-sm w-full flex lg:flex-col transition-colors duration-fast bg-nav-secondary-item-background-brand border border-nav-secondary-item-background-brand hover:bg-nav-secondary-item-background-brand-hover'
														onClick={() => {
															props.setActiveTab(undefined);
														}}
														data-brand={card.brand}
														data-theme={theme}
													>
														<div className='p-spacing-xs flex flex-col justify-center gap-0.5 grow'>
															{card.title && (
																<Text className='inline-block' styleType='heading-4xs'>
																	{card.title}

																	{isExternal && (
																		<IconResolver className='text-text-brand ml-2 inline-block w-4 h-4' icon='arrow-square-out' />
																	)}
																</Text>
															)}

															{card.description && (
																<Text className='text-text-secondary' styleType='body-sm'>
																	{card.description}
																</Text>
															)}
														</div>

														{card.image && (
															<Media
																className='mt-auto aspect-square flex-[0_0_7.5rem] overflow-hidden [&_img]:h-full h-full sm:flex-[0_0_10rem] lg:h-auto'
																image={card.image}
															/>
														)}
													</div>
												</Anchor>
											);
										})}
									</div>
								)}

								{group.items && group.items.length > 0 && (
									<div className='[flex-basis:0] grow-1 flex flex-col gap-1'>
										{group.items.map((item, j) => {
											const isExternal: boolean = !(item.href?.startsWith('/') || item.href?.startsWith(process.env.NEXT_PUBLIC_SITE_URL));
											return (
												<Anchor key={`${item.title}-${j}-mobile-tab-data`} href={item.href} target={isExternal ? '_blank' : undefined}>
													<div
														className='rounded-sm p-spacing-3xs flex items-center gap-spacing-2xs transition-colors duration-fast hover:bg-nav-secondary-item-background-brand-subtle-hover max-lg:-mx-spacing-3xs'
														onClick={() => {
															props.setActiveTab(undefined);
														}}
														data-brand={item.brand}
														data-theme={theme}
													>
														{item.image && (
															<Media
																className='bg-background-secondary shrink-0 aspect-square rounded-sm border border-border-primary w-12 max-w-12 overflow-hidden [&_img]:h-full h-fit md:h-auto'
																image={item.image}
															/>
														)}

														<div className='min-h-12 flex flex-col justify-center gap-0.5'>
															{item.title && (
																<Text styleType='heading-5xs'>
																	{item.title}

																	{isExternal && (
																		<IconResolver className='text-text-brand ml-2 inline-block w-4 h-4' icon='arrow-square-out' />
																	)}
																</Text>
															)}

															{item.description && (
																<Text className='text-text-secondary' styleType='body-xs'>
																	{item.description}
																</Text>
															)}
														</div>
													</div>
												</Anchor>
											);
										})}
									</div>
								)}
							</div>
						</div>
					);
				})}

			{props.featuredCards && props.featuredCards.length > 0 && (
				<div className='flex flex-col gap-spacing-3xs'>
					<Text as='h3' className='text-text-secondary p-spacing-3xs' styleType='label-md'>
						{featuredText}
					</Text>

					{props.featuredCards?.map((item, i) => (
						<div
							key={`${featuredText}-${i}-mobile-tab-data`}
							onClick={() => {
								props.setActiveTab(undefined);
							}}
						>
							<Card {...item} layout='horizontal' size='sm' />
						</div>
					))}
				</div>
			)}
		</div>
	);
}
